import React, { useState, useCallback, memo, useMemo } from "react";
import {
  Dialog, DialogContent, DialogActions, Button, Box, Typography,
  TextField, Switch, FormControlLabel, Stepper, Step, StepLabel,
  StepContent, Paper, Stack, Chip, InputAdornment, MenuItem,
  Collapse, Alert, LinearProgress, IconButton, Avatar, Divider,
  useMediaQuery, Grid, FormControl, InputLabel, Select, FormHelperText,
  Card, CardContent,
} from "@mui/material";
import {
  Public as PublicIcon,
  Lock as LockIcon,
  AttachMoney as MoneyIcon,
  Image as ImageIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ChatBubble as MessageCircleIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Group as GroupIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useMutation } from "react-query";
import SelectAssetDialog from "../Components/tools/selectfile";

// Preset themes for bubble branding
const PRESET_THEMES = [
  { name: 'Midnight Blue', primary: '#111111', secondary: '#1f24d8' },
  { name: 'Ocean Blue', primary: '#0077be', secondary: '#00a8e8' },
  { name: 'Sunset Orange', primary: '#ff6b35', secondary: '#ff9f1c' },
  { name: 'Forest Green', primary: '#2d6a4f', secondary: '#52b788' },
  { name: 'Royal Purple', primary: '#5a189a', secondary: '#9d4edd' },
  { name: 'Rose Gold', primary: '#d4af37', secondary: '#f7cac9' },
  { name: 'Night Sky', primary: '#1a1a2e', secondary: '#16213e' },
  { name: 'Coral Reef', primary: '#ff6b6b', secondary: '#4ecdc4' },
  { name: 'Lavender Dreams', primary: '#a8dadc', secondary: '#e63946' },
];

const STEP_DETAILS = [
  { title: 'Basic Information', Icon: DescriptionIcon },
  { title: 'Visibility & Access', Icon: SettingsIcon },
  { title: 'Pricing', Icon: MoneyIcon },
  { title: 'Branding & Media', Icon: PaletteIcon, optional: true },
];

const DEFAULT_BUBBLE_THEME = {
  primary: '#111111',
  secondary: '#1f24d8',
};

const NEUTRAL_DARK = '#111111';
const NEUTRAL_BORDER = '#d7d7db';
const NEUTRAL_MUTED = '#6b7280';

const previewChipSx = {
  borderRadius: 999,
  fontWeight: 600,
  bgcolor: 'white',
  color: NEUTRAL_DARK,
  border: `1px solid ${NEUTRAL_BORDER}`,
  '& .MuiChip-icon': {
    color: NEUTRAL_MUTED,
  },
};

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED PREVIEW CARD - OUTSIDE MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const SummaryPreviewCard = memo(({ 
  bubbleData,
}) => {
  return (
    <Card
      elevation={3}
      sx={{
        overflow: 'hidden',
        position: 'sticky',
        top: 16,
      }}
    >
      {/* Cover Image Preview */}
      <Box
        sx={{
          height: 140,
          background: bubbleData.coverImage
            ? `url(${bubbleData.coverImage})`
            : `linear-gradient(135deg, ${bubbleData.themeColors.primary} 0%, ${bubbleData.themeColors.secondary} 100%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        {/* Avatar Preview */}
        <Avatar
          src={bubbleData.avatarImage}
          sx={{
            width: 80,
            height: 80,
            border: '4px solid white',
            position: 'absolute',
            bottom: -40,
            left: 24,
            bgcolor: bubbleData.avatarImage ? 'transparent' : bubbleData.themeColors.primary,
            fontSize: '2rem',
            fontWeight: 700,
          }}
        >
          {!bubbleData.avatarImage && (bubbleData.name.charAt(0).toUpperCase() || 'B')}
        </Avatar>
      </Box>

      <CardContent sx={{ pt: 6, pb: 3 }}>
        <Stack spacing={2.5}>
          {/* Name & Description */}
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {bubbleData.name || 'Untitled Bubble'}
            </Typography>
            {bubbleData.description && (
              <Typography variant="body2" color="text.secondary">
                {bubbleData.description}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Status Chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip
              size="small"
              icon={bubbleData.isPublic ? <PublicIcon /> : <LockIcon />}
              label={bubbleData.isPublic ? "Public" : "Private"}
              sx={previewChipSx}
            />
            {bubbleData.isPaid ? (
              <Chip
                size="small"
                icon={<MoneyIcon />}
                label={bubbleData.isSubscription 
                  ? `$${bubbleData.accessPrice || '0'}/${bubbleData.subscriptionInterval}` 
                  : `$${bubbleData.accessPrice || '0'} one-time`}
                sx={previewChipSx}
              />
            ) : (
              <Chip size="small" label="Free" sx={previewChipSx} />
            )}
            {bubbleData.createChat && (
              <Chip
                size="small"
                icon={<MessageCircleIcon />}
                label="Chat Enabled"
                sx={previewChipSx}
              />
            )}
          </Stack>

          {/* Category */}
          {bubbleData.category && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Category
              </Typography>
              <Chip
                size="small"
                label={bubbleData.category}
                variant="outlined"
              />
            </Box>
          )}

          {/* Tags */}
          {bubbleData.tags.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Tags
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                {bubbleData.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Theme Options
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={1} justifyContent="space-between">
                {PRESET_THEMES.slice(0, 4).map((presetTheme) => (
                  <Box
                    key={presetTheme.name}
                    sx={{
                      flex: 1,
                      height: 34,
                      borderRadius: 999,
                      background: `linear-gradient(135deg, ${presetTheme.primary} 0%, ${presetTheme.secondary} 100%)`,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                ))}
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.bubbleData) === JSON.stringify(nextProps.bubbleData);
});

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED FORM CONTENT - OUTSIDE MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const BubbleFormContent = memo(({ 
  error,
  setError,
  activeStep,
  bubbleData,
  handleInputChange,
  isMobile,
  tagInput,
  setTagInput,
  handleAddTag,
  handleRemoveTag,
  theme,
  handleThemeSelect,
  handleThemeColorChange,
  handleOpenAssetDialog,
  steps
}) => {
  const renderedActiveStep = isMobile ? activeStep : 0;

  return (
    <>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!isMobile && (
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
          {STEP_DETAILS.map((step, index) => {
            const isCompleted = index < activeStep;
            const isCurrent = index === activeStep;

            return (
              <Chip
                key={step.title}
                icon={
                  isCompleted
                    ? <CheckCircleIcon fontSize="small" />
                    : <step.Icon fontSize="small" />
                }
                label={step.title}
                variant={isCurrent ? "filled" : "outlined"}
                sx={{
                  borderRadius: 999,
                  fontWeight: isCurrent ? 700 : 600,
                  bgcolor: isCurrent ? NEUTRAL_DARK : 'white',
                  color: isCurrent ? 'white' : NEUTRAL_DARK,
                  borderColor: isCurrent ? NEUTRAL_DARK : NEUTRAL_BORDER,
                  '& .MuiChip-icon': {
                    color: isCurrent ? 'white' : isCompleted ? NEUTRAL_DARK : NEUTRAL_MUTED,
                  },
                }}
              />
            );
          })}
        </Stack>
      )}

      <Box
        sx={!isMobile ? { p: 0 } : undefined}
      >
      <Stepper
        activeStep={renderedActiveStep}
        orientation="vertical"
        sx={{
          '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
            color: NEUTRAL_DARK,
          },
          '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
            color: `${NEUTRAL_DARK} !important`,
            fontWeight: 700,
          },
          '& .MuiStepConnector-line': {
            borderColor: NEUTRAL_BORDER,
          },
        }}
      >
        {/* Step 0: Basic Information */}
        {(isMobile || activeStep === 0) && (
        <Step>
          <StepLabel>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DescriptionIcon fontSize="small" />
              <span>Basic Information</span>
            </Stack>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack spacing={3}>
              <TextField
                label="Bubble Name"
                value={bubbleData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                fullWidth
                required
                helperText={`${bubbleData.name.length}/255 characters`}
                error={bubbleData.name.length > 255}
                placeholder="e.g., Marketing Team, Book Club, Fitness Group"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <TextField
                label="Description"
                value={bubbleData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                fullWidth
                multiline
                rows={4}
                helperText="Tell members what this bubble is about"
                placeholder="Describe the purpose and goals of this community..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <FormControl fullWidth>
                <InputLabel>Category (Optional)</InputLabel>
                <Select
                  value={bubbleData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="Category (Optional)"
                  sx={{ borderRadius: 2 }}
                  MenuProps={{
                    disablePortal: true,
                    keepMounted: false,
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="business">Business & Professional</MenuItem>
                  <MenuItem value="creative">Creative & Arts</MenuItem>
                  <MenuItem value="education">Education & Learning</MenuItem>
                  <MenuItem value="entertainment">Entertainment & Media</MenuItem>
                  <MenuItem value="fitness">Health & Fitness</MenuItem>
                  <MenuItem value="gaming">Gaming</MenuItem>
                  <MenuItem value="lifestyle">Lifestyle</MenuItem>
                  <MenuItem value="technology">Technology</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
                <FormHelperText>Help members discover your bubble</FormHelperText>
              </FormControl>

              <Box>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600} 
                  sx={{ mb: 1.5, fontSize: isMobile ? '0.875rem' : '1rem' }}
                >
                  Tags (Optional)
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add a tag (e.g., marketing, design)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim()}
                    sx={{ 
                      borderRadius: 2, 
                      minWidth: isMobile ? 70 : 80,
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                  >
                    Add
                  </Button>
                </Stack>
                {bubbleData.tags.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {bubbleData.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        onDelete={() => handleRemoveTag(index)}
                        variant="outlined"
                        color="primary"
                        size={isMobile ? "small" : "medium"}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </StepContent>
        </Step>
        )}

        {/* Step 1: Visibility & Access */}
        {(isMobile || activeStep === 1) && (
        <Step>
          <StepLabel>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SettingsIcon fontSize="small" />
              <span>Visibility & Access</span>
            </Stack>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: isMobile ? 2 : 2.5,
                  border: '2px solid',
                  borderColor: bubbleData.isPublic ? 'primary.main' : 'warning.main',
                  bgcolor: bubbleData.isPublic 
                    ? alpha(theme.palette.primary.main, 0.05) 
                    : alpha(theme.palette.warning.main, 0.05),
                  borderRadius: 2
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={bubbleData.isPublic}
                      onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1.5}>
                      {bubbleData.isPublic ? <PublicIcon /> : <LockIcon />}
                      <Box>
                        <Typography 
                          variant={isMobile ? "body2" : "body1"} 
                          fontWeight={600}
                        >
                          {bubbleData.isPublic ? "Public Bubble" : "Private Bubble"}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                        >
                          {bubbleData.isPublic
                            ? "Anyone can discover and join this bubble"
                            : "Only invited members can join"}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: isMobile ? 2 : 2.5,
                  border: '2px solid',
                  borderColor: bubbleData.createChat ? 'info.main' : 'divider',
                  bgcolor: bubbleData.createChat 
                    ? alpha(theme.palette.info.main, 0.05) 
                    : 'transparent',
                  borderRadius: 2
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={bubbleData.createChat}
                      onChange={(e) => handleInputChange('createChat', e.target.checked)}
                      color="info"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <MessageCircleIcon />
                      <Box>
                        <Typography 
                          variant={isMobile ? "body2" : "body1"} 
                          fontWeight={600}
                        >
                          Create Bubble Chat
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                        >
                          Automatically create a conversation thread for members
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Paper>
            </Stack>
          </StepContent>
        </Step>
        )}

        {/* Step 2: Pricing */}
        {(isMobile || activeStep === 2) && (
        <Step>
          <StepLabel>
            <Stack direction="row" alignItems="center" spacing={1}>
              <MoneyIcon fontSize="small" />
              <span>Pricing</span>
            </Stack>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: isMobile ? 2 : 2.5,
                  border: '2px solid',
                  borderColor: bubbleData.isPaid ? 'success.main' : 'divider',
                  bgcolor: bubbleData.isPaid 
                    ? alpha(theme.palette.success.main, 0.05) 
                    : 'transparent',
                  borderRadius: 2
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={bubbleData.isPaid}
                      onChange={(e) => handleInputChange('isPaid', e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <MoneyIcon />
                      <Box>
                        <Typography 
                          variant={isMobile ? "body2" : "body1"} 
                          fontWeight={600}
                        >
                          Paid Access
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                        >
                          Require payment to join this bubble
                        </Typography>
                      </Box>
                    </Box>
                  }
                />

                <Collapse in={bubbleData.isPaid}>
                  <Stack spacing={2.5} sx={{ mt: 3, pl: 0 }}>
                    <TextField
                      label="Access Price"
                      value={bubbleData.accessPrice}
                      onChange={(e) => handleInputChange('accessPrice', e.target.value)}
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      fullWidth
                      required={bubbleData.isPaid}
                      helperText="One-time access fee or subscription price"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />

                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={bubbleData.isSubscription}
                            onChange={(e) => handleInputChange('isSubscription', e.target.checked)}
                            size="small"
                          />
                        }
                        label={
                          <Typography 
                            variant={isMobile ? "caption" : "body2"} 
                            fontWeight={600}
                          >
                            Recurring Subscription
                          </Typography>
                        }
                      />

                      <Collapse in={bubbleData.isSubscription}>
                        <TextField
                          select
                          value={bubbleData.subscriptionInterval}
                          onChange={(e) => handleInputChange('subscriptionInterval', e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          SelectProps={{
                            MenuProps: {
                              disablePortal: true,
                              keepMounted: false,
                            }
                          }}
                        >
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="quarterly">Quarterly</MenuItem>
                          <MenuItem value="yearly">Yearly</MenuItem>
                        </TextField>
                      </Collapse>
                    </Box>

                    {!bubbleData.isPaid && (
                      <Alert severity="info" icon={<InfoIcon />}>
                        This bubble will be free to join
                      </Alert>
                    )}
                  </Stack>
                </Collapse>
              </Paper>
            </Stack>
          </StepContent>
        </Step>
        )}

        {/* Step 3: Branding & Media */}
        {(isMobile || activeStep === 3) && (
        <Step>
          <StepLabel>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PaletteIcon fontSize="small" />
              <span>Branding & Media</span>
              <Typography variant="caption" color="text.secondary">
                Optional
              </Typography>
            </Stack>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack spacing={3}>
              {/* Theme Colors */}
              <Box>
                <Typography 
                  variant={isMobile ? "body1" : "subtitle1"} 
                  fontWeight={700} 
                  sx={{ mb: 2 }}
                >
                  Theme Colors
                </Typography>
                <Typography 
                  variant={isMobile ? "caption" : "body2"} 
                  color="text.secondary" 
                  sx={{ mb: 2 }}
                >
                  Choose colors for your bubble page
                </Typography>

                {/* Preset Themes */}
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {PRESET_THEMES.map((presetTheme) => (
                    <Grid item xs={6} sm={3} key={presetTheme.name}>
                      <Paper
                        onClick={() => handleThemeSelect(presetTheme)}
                        sx={{
                          p: isMobile ? 1 : 1.5,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor:
                            bubbleData.themeColors.primary === presetTheme.primary &&
                            bubbleData.themeColors.secondary === presetTheme.secondary
                              ? 'primary.main'
                              : 'divider',
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: theme.shadows[4]
                          }
                        }}
                      >
                        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                          <Box
                            sx={{
                              width: '50%',
                              height: isMobile ? 32 : 40,
                              bgcolor: presetTheme.primary,
                              borderRadius: 1
                            }}
                          />
                          <Box
                            sx={{
                              width: '50%',
                              height: isMobile ? 32 : 40,
                              bgcolor: presetTheme.secondary,
                              borderRadius: 1
                            }}
                          />
                        </Stack>
                        <Typography 
                          variant="caption" 
                          fontWeight={600} 
                          sx={{ 
                            display: 'block', 
                            textAlign: 'center',
                            fontSize: isMobile ? '0.65rem' : '0.75rem'
                          }}
                        >
                          {presetTheme.name}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {/* Custom Color Pickers */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Primary Color"
                      type="color"
                      fullWidth
                      value={bubbleData.themeColors.primary}
                      onChange={(e) => handleThemeColorChange('primary', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Secondary Color"
                      type="color"
                      fullWidth
                      value={bubbleData.themeColors.secondary}
                      onChange={(e) => handleThemeColorChange('secondary', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Cover Image */}
              <Box>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600} 
                  sx={{ mb: 1.5, fontSize: isMobile ? '0.875rem' : '1rem' }}
                >
                  Cover Image
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  display="block" 
                  sx={{ mb: 2, fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                >
                  Banner image displayed at the top of your bubble
                </Typography>

                {bubbleData.coverImage ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={bubbleData.coverImage}
                      sx={{ 
                        width: isMobile ? 48 : 60, 
                        height: isMobile ? 48 : 60 
                      }}
                    >
                      <ImageIcon />
                    </Avatar>
                    <Typography 
                      variant={isMobile ? "caption" : "body2"} 
                      sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {bubbleData.coverImage}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleInputChange('coverImage', '')}
                      color="error"
                    >
                      <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Paper>
                ) : (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenAssetDialog('cover')}
                    sx={{ 
                      borderRadius: 2, 
                      py: isMobile ? 1.25 : 1.5,
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                  >
                    Select Cover Image
                  </Button>
                )}
              </Box>

              {/* Avatar Image */}
              <Box>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600} 
                  sx={{ mb: 1.5, fontSize: isMobile ? '0.875rem' : '1rem' }}
                >
                  Avatar Image
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  display="block" 
                  sx={{ mb: 2, fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                >
                  Small icon representing your bubble
                </Typography>

                {bubbleData.avatarImage ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={bubbleData.avatarImage}
                      sx={{ 
                        width: isMobile ? 48 : 60, 
                        height: isMobile ? 48 : 60 
                      }}
                    >
                      <ImageIcon />
                    </Avatar>
                    <Typography 
                      variant={isMobile ? "caption" : "body2"} 
                      sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {bubbleData.avatarImage}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleInputChange('avatarImage', '')}
                      color="error"
                    >
                      <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Paper>
                ) : (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenAssetDialog('avatar')}
                    sx={{ 
                      borderRadius: 2, 
                      py: isMobile ? 1.25 : 1.5,
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                  >
                    Select Avatar Image
                  </Button>
                )}
              </Box>

              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="caption">
                  You can update images and customize additional settings after creating your bubble.
                </Typography>
              </Alert>
            </Stack>
          </StepContent>
        </Step>
        )}
      </Stepper>
      </Box>

      {/* Summary Preview - Mobile only */}
      {isMobile && activeStep === steps.length - 1 && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <CheckCircleIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>
              Summary
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                Name
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {bubbleData.name || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                Visibility
              </Typography>
              <Chip
                size="small"
                label={bubbleData.isPublic ? "Public" : "Private"}
                icon={bubbleData.isPublic ? <PublicIcon /> : <LockIcon />}
                color={bubbleData.isPublic ? "primary" : "default"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                Pricing
              </Typography>
              {bubbleData.isPaid ? (
                <Chip
                  size="small"
                  label={bubbleData.isSubscription 
                    ? `$${bubbleData.accessPrice}/${bubbleData.subscriptionInterval}` 
                    : `$${bubbleData.accessPrice} one-time`}
                  icon={<MoneyIcon />}
                  color="success"
                />
              ) : (
                <Chip size="small" label="Free" color="default" />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                Features
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                {bubbleData.createChat && (
                  <Chip size="small" label="Chat" variant="outlined" />
                )}
                {bubbleData.coverImage && (
                  <Chip size="small" label="Cover" variant="outlined" />
                )}
                {bubbleData.avatarImage && (
                  <Chip size="small" label="Avatar" variant="outlined" />
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
});

export default function CreateBubbleDialog({ open, onClose, onSuccess, API }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);

  // Form state
  const [bubbleData, setBubbleData] = useState({
    name: "",
    description: "",
    isPublic: true,
    isPaid: false,
    accessPrice: "",
    isSubscription: false,
    subscriptionInterval: "monthly",
    createChat: true,
    coverImage: "",
    avatarImage: "",
    themeColors: DEFAULT_BUBBLE_THEME,
    category: "",
    tags: []
  });

  const [tagInput, setTagInput] = useState("");
  const [error, setError] = useState(null);

  // Asset selection state
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [assetSelectionMode, setAssetSelectionMode] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const createMutation = useMutation(
    (data) => API.bubbles.create(data),
    {
      onSuccess: () => {
        onSuccess?.();
        handleClose();
      },
      onError: (err) => {
        setError(err?.response?.data?.error || "Failed to create bubble");
      },
    }
  );

  const handleClose = useCallback(() => {
    setBubbleData({
      name: "",
      description: "",
      isPublic: true,
      isPaid: false,
      accessPrice: "",
      isSubscription: false,
      subscriptionInterval: "monthly",
      createChat: true,
      coverImage: "",
      avatarImage: "",
      themeColors: DEFAULT_BUBBLE_THEME,
      category: "",
      tags: []
    });
    setActiveStep(0);
    setTagInput("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleInputChange = useCallback((field, value) => {
    setBubbleData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleThemeSelect = useCallback((presetTheme) => {
    setBubbleData(prev => ({
      ...prev,
      themeColors: {
        primary: presetTheme.primary,
        secondary: presetTheme.secondary
      }
    }));
  }, []);

  const handleThemeColorChange = useCallback((colorType, value) => {
    setBubbleData(prev => ({
      ...prev,
      themeColors: {
        ...prev.themeColors,
        [colorType]: value
      }
    }));
  }, []);

  const handleAddTag = useCallback(() => {
    if (tagInput.trim() && !bubbleData.tags.includes(tagInput.trim())) {
      setBubbleData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  }, [tagInput, bubbleData.tags]);

  const handleRemoveTag = useCallback((index) => {
    setBubbleData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  }, []);

  // Asset dialog handlers
  const handleOpenAssetDialog = useCallback((mode) => {
    setAssetSelectionMode(mode);
    setAssetDialogOpen(true);
  }, []);

  const handleAssetClick = useCallback((asset) => {
    setSelectedAsset(asset);
  }, []);

  const handleAssetDialogClose = useCallback(() => {
    if (selectedAsset) {
      const imageUrl = selectedAsset.file_url || selectedAsset.filename;
      if (assetSelectionMode === 'cover') {
        setBubbleData(prev => ({ ...prev, coverImage: imageUrl }));
      } else if (assetSelectionMode === 'avatar') {
        setBubbleData(prev => ({ ...prev, avatarImage: imageUrl }));
      }
    }
    
    setAssetDialogOpen(false);
    setSelectedAsset(null);
    setAssetSelectionMode(null);
  }, [selectedAsset, assetSelectionMode]);

  const handleSubmit = useCallback(() => {
    if (!bubbleData.name.trim()) {
      setError("Bubble name is required");
      return;
    }

    if (bubbleData.isPaid && (!bubbleData.accessPrice || parseFloat(bubbleData.accessPrice) <= 0)) {
      setError("Please enter a valid access price");
      return;
    }

    const payload = {
      name: bubbleData.name.trim(),
      description: bubbleData.description.trim() || null,
      is_public: bubbleData.isPublic,
      is_paid: bubbleData.isPaid,
      create_chat: bubbleData.createChat,
    };

    if (bubbleData.isPaid) {
      payload.access_price = parseFloat(bubbleData.accessPrice);
      payload.is_subscription = bubbleData.isSubscription;
      if (bubbleData.isSubscription) {
        payload.subscription_interval = bubbleData.subscriptionInterval;
      }
    }

    if (bubbleData.coverImage) {
      payload.cover_image = bubbleData.coverImage;
    }
    if (bubbleData.avatarImage) {
      payload.avatar_image = bubbleData.avatarImage;
    }
    if (bubbleData.themeColors.primary || bubbleData.themeColors.secondary) {
      payload.theme_colors = bubbleData.themeColors;
    }
    if (bubbleData.category) {
      payload.category = bubbleData.category;
    }
    if (bubbleData.tags.length > 0) {
      payload.tags = bubbleData.tags;
    }

    createMutation.mutate(payload);
  }, [bubbleData, createMutation]);

  const canProceedFromStep = useCallback((step) => {
    switch (step) {
      case 0:
        return bubbleData.name.trim().length > 0 && bubbleData.name.length <= 255;
      case 1:
        return true;
      case 2:
        if (bubbleData.isPaid) {
          return bubbleData.accessPrice && parseFloat(bubbleData.accessPrice) > 0;
        }
        return true;
      case 3:
        return true;
      default:
        return true;
    }
  }, [bubbleData.name, bubbleData.isPaid, bubbleData.accessPrice]);

  const handleNext = useCallback(() => {
    if (canProceedFromStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  }, [activeStep, canProceedFromStep]);

  const handleBack = useCallback(() => {
    setActiveStep(prev => prev - 1);
  }, []);

  const steps = STEP_DETAILS.map((step) => step.title);

  const progress = ((activeStep + 1) / steps.length) * 100;

  // Memoize form content props
  const formContentProps = useMemo(() => ({
    error,
    setError,
    activeStep,
    bubbleData,
    handleInputChange,
    isMobile,
    tagInput,
    setTagInput,
    handleAddTag,
    handleRemoveTag,
    theme,
    handleThemeSelect,
    handleThemeColorChange,
    handleOpenAssetDialog,
    steps
  }), [
    error,
    activeStep,
    bubbleData,
    isMobile,
    tagInput,
    theme,
    steps,
    handleInputChange,
    handleAddTag,
    handleRemoveTag,
    handleThemeSelect,
    handleThemeColorChange,
    handleOpenAssetDialog
  ]);

  if (!open) return null;

  // Mobile View
  if (isMobile) {
    return (
      <>
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="md"
          fullWidth
          fullScreen
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 0,
              boxShadow: 'none',
              m: 0,
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              height: '100vh',
              maxHeight: '100vh',
            }
          }}
        >
          {/* Header with Progress */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              boxShadow: theme.shadows[4],
              pt: 'max(16px, env(safe-area-inset-top, 0px))',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(circle at 20% 80%, ${alpha(theme.palette.secondary.main, 0.3)} 0%, transparent 70%)`,
                pointerEvents: 'none'
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1, p: 2, pb: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                    Create New Bubble
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Step {activeStep + 1} of {steps.length} • {steps[activeStep]}
                  </Typography>
                </Box>

                <IconButton
                  onClick={handleClose}
                  size="small"
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'white',
                    borderRadius: 3,
                  }
                }}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {steps[activeStep]}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.95, fontWeight: 700 }}>
                  {Math.round(progress)}%
                </Typography>
              </Stack>
            </Box>
          </Box>

          <DialogContent
            sx={{
              p: 2,
              bgcolor: 'background.default',
              overflowY: 'auto',
              flex: 1,
              WebkitOverflowScrolling: 'touch',
              paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            }}
          >
            <BubbleFormContent {...formContentProps} />
          </DialogContent>

          <Divider />

          <DialogActions 
            sx={{ 
              p: 2, 
              gap: 1, 
              bgcolor: 'background.paper',
              paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
              justifyContent: 'space-between',
            }}
          >
            <Button
              onClick={handleClose}
              variant="text"
              size="medium"
              disabled={createMutation.isLoading}
              sx={{ borderRadius: 2, fontSize: '0.875rem', minWidth: 88 }}
            >
              Cancel
            </Button>

            <Stack direction="row" spacing={1}>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  variant="outlined"
                  size="medium"
                  startIcon={<ArrowBackIcon />}
                  disabled={createMutation.isLoading}
                  sx={{ borderRadius: 2, fontSize: '0.875rem' }}
                >
                  Back
                </Button>
              )}

              {activeStep < steps.length - 1 ? (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  size="medium"
                  disabled={!canProceedFromStep(activeStep)}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ borderRadius: 2, minWidth: 90, fontSize: '0.875rem' }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  size="medium"
                  disabled={createMutation.isLoading || !canProceedFromStep(activeStep)}
                  sx={{ borderRadius: 2, minWidth: 110, fontSize: '0.875rem' }}
                >
                  {createMutation.isLoading ? "Creating..." : "Create Bubble"}
                </Button>
              )}
            </Stack>
          </DialogActions>
        </Dialog>

        <SelectAssetDialog
          open={assetDialogOpen}
          onClose={handleAssetDialogClose}
          selectedAsset={selectedAsset}
          onAssetClick={handleAssetClick}
        />
      </>
    );
  }

  // Desktop View - Full Screen Overlay
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        PaperProps={{
          sx: {
            m: 0,
            width: '100vw',
            height: '100vh',
            maxHeight: '100vh',
            maxWidth: '100vw',
            borderRadius: 0,
            display: 'flex',
            overflow: 'hidden',
            bgcolor: 'background.default',
          }
        }}
        disablePortal={false}
        keepMounted={false}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'stretch',
          }
        }}
      >
        {/* Header Bar */}
        <Box
          sx={{
            height: 80,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 4,
            bgcolor: 'background.paper',
            backdropFilter: 'blur(20px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                background: NEUTRAL_DARK,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <GroupIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Create New Bubble
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Step {activeStep + 1} of {steps.length} • {steps[activeStep]}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ minWidth: 220 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 0.75 }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Progress
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {Math.round(progress)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  width: '100%',
                  height: 6,
                  borderRadius: 999,
                  bgcolor: '#ececef',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    backgroundColor: NEUTRAL_DARK,
                  }
                }}
              />
            </Box>
            <IconButton 
              onClick={handleClose}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Main Content - Two Column Layout */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left Column - Preview */}
          <Box
            sx={{
              width: 380,
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              overflowY: 'auto',
              p: 3,
            }}
          >
            <SummaryPreviewCard 
              bubbleData={bubbleData}
            />
          </Box>

          {/* Right Column - Form */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: 4,
                pt: 4,
                pb: 3,
              }}
            >
              <BubbleFormContent {...formContentProps} />
            </Box>

            <Box
              sx={{
                px: 4,
                py: 2.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0 -1px 0 rgba(17,17,17,0.03)',
              }}
            >
              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button
                  onClick={handleClose}
                  variant="text"
                  size="medium"
                  disabled={createMutation.isLoading}
                  sx={{ borderRadius: 2, px: 2.5, color: NEUTRAL_DARK }}
                >
                  Cancel
                </Button>

                {activeStep > 0 && (
                  <Button
                    onClick={handleBack}
                    variant="outlined"
                    size="medium"
                    startIcon={<ArrowBackIcon />}
                    disabled={createMutation.isLoading}
                    sx={{ borderRadius: 2, px: 2.5, color: NEUTRAL_DARK, borderColor: NEUTRAL_BORDER }}
                  >
                    Back
                  </Button>
                )}

                {activeStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    size="medium"
                    disabled={!canProceedFromStep(activeStep)}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ borderRadius: 2, px: 3, bgcolor: NEUTRAL_DARK, '&:hover': { bgcolor: '#000000' } }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    size="medium"
                    disabled={createMutation.isLoading || !canProceedFromStep(activeStep)}
                    sx={{ borderRadius: 2, px: 3, bgcolor: NEUTRAL_DARK, '&:hover': { bgcolor: '#000000' } }}
                  >
                    {createMutation.isLoading ? "Creating..." : "Create Bubble"}
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <SelectAssetDialog
        open={assetDialogOpen}
        onClose={handleAssetDialogClose}
        selectedAsset={selectedAsset}
        onAssetClick={handleAssetClick}
      />
    </>
  );
}
